import { GlobalConfigType } from 'actions'
import { FieldOrigin, getMappedProductType } from 'config'
import {
  CreditReportDefaultsConfig,
  CreditReportDefaultsRules,
  CreditScoreSelectionMethod,
  defaultCreditReportDefaultsConfig,
} from 'pages/Admin/AdminTools/Configuration/CreditReportDefaults/constants'
import { store } from 'reducers'

import { BorrowerSeperator } from '../config'

export type BorrowerCreditScores = {
  equifax?: string | number | null
  transunion?: string | number | null
  experian?: string | number | null
  priceScore?: string | number | null
  isIncluded?: boolean
  borrowerSeperator?: string
}

const isPrimaryBorrower = (b: BorrowerCreditScores, index: number): boolean => {
  if (b.borrowerSeperator != null) return b.borrowerSeperator === BorrowerSeperator.BORROWER
  return index === 0
}

export type DetermineCreditScoreResult = {
  score: number | null
  isNoScore: boolean
  borrowerMidScores: Array<number | null>
}

const parseScore = (val: any): number | null => {
  if (val === undefined || val === null || val === '') return null
  const str = String(val).trim().toUpperCase()
  if (str === 'N/A' || str === 'NA') return null
  const num = Number(str.replace(/,/g, ''))
  if (!num || num <= 0 || Number.isNaN(num)) return null
  return num
}

/** Mid score for a single borrower's bureau scores, applying configured edge-case rules. */
export const getBorrowerMidScore = (
  equifax: any,
  transunion: any,
  experian: any,
  rules: CreditReportDefaultsRules,
): number | null => {
  const scores = [parseScore(equifax), parseScore(transunion), parseScore(experian)].filter(
    (s): s is number => s !== null,
  )

  if (scores.length === 3) {
    const minValue = Math.min(...scores)
    const maxValue = Math.max(...scores)
    return scores.reduce((a, b) => a + b, 0) - minValue - maxValue
  }

  if (scores.length === 2) {
    if (!rules.twoScoresUseLower) return null
    return Math.min(...scores)
  }

  if (scores.length === 1) {
    if (!rules.oneScoreUseAsMid) return null
    return scores[0]
  }

  if (rules.noScoreAsNA) return null
  return null
}

/**
 * Determine loan credit score from product type config and borrowers' bureau scores.
 * Primary borrower's priceScore overrides the aggregate when set.
 */
export const determineCreditScore = (
  productType: string,
  borrowers: BorrowerCreditScores[],
  config?: CreditReportDefaultsConfig | null,
  options?: { includeCoborrower?: boolean; ignorePriceScore?: boolean },
): DetermineCreditScoreResult => {
  const cfg = config || defaultCreditReportDefaultsConfig()
  const rules: CreditReportDefaultsRules = { ...defaultCreditReportDefaultsConfig().rules, ...(cfg.rules || {}) }
  const loanFields = (store.getState() as any)?.globalConfigReducer?.[GlobalConfigType.LoanFields] || {}
  const productTypeForRules = getMappedProductType(productType, loanFields[FieldOrigin.LoanStructure] || {})
  const method =
    cfg.byProductType?.[productType] ||
    cfg.byProductType?.[productTypeForRules] ||
    CreditScoreSelectionMethod.LowestMidScore

  // Missing includeCoborrower means exclude coborrowers (do not default to include).
  const includeCoborrower = !!options?.includeCoborrower
  const eligible = borrowers.filter((b, index) => {
    if (isPrimaryBorrower(b, index)) return true
    if (!includeCoborrower) return false
    return b.isIncluded !== false
  })

  const borrowerMidScores = eligible.map((b) => getBorrowerMidScore(b.equifax, b.transunion, b.experian, rules))

  // Manual priceScore override on primary borrower (skipped when ignorePriceScore)
  if (!options?.ignorePriceScore) {
    const primary = borrowers.find((b, index) => isPrimaryBorrower(b, index)) || borrowers[0]
    const priceScore = primary ? parseScore(primary.priceScore) : null
    if (priceScore !== null) {
      return { score: priceScore, isNoScore: false, borrowerMidScores }
    }
  }

  const numericScores = borrowerMidScores.filter((s): s is number => s !== null)
  const hasNA = borrowerMidScores.some((s) => s === null)

  if (numericScores.length === 0) {
    return { score: null, isNoScore: true, borrowerMidScores }
  }

  if (method === CreditScoreSelectionMethod.HighestMidScore) {
    return {
      score: Math.max(...numericScores),
      isNoScore: false,
      borrowerMidScores,
    }
  }

  // Lowest Mid Score — N/A as lesser means overall N/A
  if (hasNA && rules.noScoreAsLesserWhenLowest) {
    return { score: null, isNoScore: true, borrowerMidScores }
  }

  return {
    score: Math.min(...numericScores),
    isNoScore: false,
    borrowerMidScores,
  }
}

/** Module-level cache so sync validators can reuse the last-loaded admin config. */
let cachedCreditReportDefaults: CreditReportDefaultsConfig | null = null

export const setCachedCreditReportDefaults = (config: CreditReportDefaultsConfig | null) => {
  cachedCreditReportDefaults = config
}

export const getCachedCreditReportDefaults = (): CreditReportDefaultsConfig =>
  cachedCreditReportDefaults || defaultCreditReportDefaultsConfig()
